import React, { useState, useRef} from "react";
import { X, Calendar } from "lucide-react";
import ModalMod from "../../../../../components/Model_Mod";
import { useDispatch } from "react-redux";
import { setOptimizerDemoDateRange } from "../../../../../store/state/GNAOptimizer/DemoOptimizer/OptimizerDate";
import { setOptimizerDemoSession } from "../../../../../store/state/GNAOptimizer/DemoOptimizer/OptimizerSession";
import { setOptimizerDemoSessionToken } from "../../../../../store/state/GNAOptimizer/DemoOptimizer/OptimizerSessionToken";
import {
  fetchOptimizerCurrentOptimisation,
  fetchOptimizerSession,
  fetchOptimizerSguGeneration,
} from "../../../../../Rest_api/DemoRestApi";
import { useOptimizerNavigation } from "../../useOptimizerNavigation";
import { ErrorPopup } from "../../../../../components/ErrorHandling";
import {
  OptimizierDataUpdate,
  OptimizierCurrentUpdate,
} from "../../../../../store/state/GNAOptimizer/CurrentOptimisationState";

const CreateNewSessionModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { navigateToPage } = useOptimizerNavigation();
  const dispatch = useDispatch();
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const startDateInputRef = useRef<HTMLInputElement>(null);
  const endDateInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.value;
    setStartDate(selected);
    // If end date is before start date, clear it
    if (endDate && selected > endDate) {
      setEndDate("");
    }
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.value;
    setEndDate(selected);
  };

  const GotoGeneration = async () => {
    if (!startDate) {
      setError("Please select a start date before proceeding.");
      return;
    }

    if (!endDate) {
      setError("Please select an end date before proceeding.");
      return;
    }

    if (startDate > endDate) {
      setError("End date must be after start date.");
      return;
    }
    try {
      setLoading(true);
      const res = await fetchOptimizerSession(startDate, endDate);
      dispatch(setOptimizerDemoDateRange({ startDate, endDate }));
      dispatch(setOptimizerDemoSession(res.session));
      dispatch(setOptimizerDemoSessionToken(res.session_token));

      if (res) {
        //   // Fetch all data
        const SguData = await fetchOptimizerSguGeneration();
        const CurrentData = await fetchOptimizerCurrentOptimisation();
        //   // Store data in Redux instead of sessionStorage
        dispatch(OptimizierDataUpdate({ data: SguData }));
        dispatch(OptimizierCurrentUpdate({ current: CurrentData }));

        navigateToPage("generation");
        onClose();
        sessionStorage.removeItem("OptimizerTab");
        window.history.replaceState(
          {},
          "",
          window.location.pathname + window.location.search
        );
      }
    } catch (error) {
      console.log(error);
      setError(`Failed to create new session ! ${error}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClickStartDate = () => {
    startDateInputRef.current?.showPicker();
  };

  const handleClickEndDate = () => {
    endDateInputRef.current?.showPicker();
  };

  return (
    <>
      <ModalMod
        isOpen={isOpen}
        onClose={() => {
          onClose();
          setStartDate("");
          setEndDate("");
          setLoading(false);
        }}
        width="600px"
        showCloseButton={false}
      >
        {/* Header */}
        <div
          className="px-1 py-1"
          style={{
            backgroundColor: "#FFFFFF",
            borderBottom: "1px solid #DFEAF2",
          }}
        >
          <div className="flex items-center justify-between">
            <h2
              className="text-lg font-medium"
              style={{
                color: "#525062",
                fontFamily: "Marsden Wide",
                fontWeight: 500,
                fontSize: "18px",
                lineHeight: "100%",
              }}
            >
              Create New Session
            </h2>
            <button
              onClick={() => {
                onClose();
                setStartDate("");
                setEndDate("");
              }}
              className="hover:bg-gray-200 p-1 rounded transition-colors text-[#777777]"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-2 space-y-2">
          {/* Instructions */}
          <div className="mb-5 mt-3 bg-white rounded-lg">
            <h3 className="text-sm font-medium mb-2 text-[#34656D]">
              Instruction to Create New Session
            </h3>
            <ul className="list-disc pl-5 space-y-1 text-sm text-[#8C8A96]">
              <li>Choose scenarios for power generation portfolio</li>
              <li>Define optimization constraints and execute the model</li>
              <li>View actionable insights and graphical results</li>
            </ul>
          </div>
          <h3 className="text-sm font-medium mb-2 text-[#6F6D7C]">
            Select Date Range
          </h3>
          {/* Date Range Selector */}
          <div className="space-y-4 border border-[#DFEAF2] p-2 rounded-xl">
            <div className="grid grid-cols-2 gap-4">
              {/* Start Date */}
              <div>
                <label
                  htmlFor="start-date"
                  className="block text-sm font-medium mb-1 text-[#6F6D7C]"
                >
                  Start date
                </label>
                <div
                  className="relative flex items-center h-[39px] rounded-lg border"
                  style={{ borderColor: "#E2E2E5" }}
                  onClick={handleClickStartDate}
                >
                  <div className="absolute left-3 pointer-events-none">
                    <Calendar size={18} className="text-[#8C8A96]" />
                  </div>
                  <input
                    id="start-date"
                    type="date"
                    value={startDate}
                    onChange={handleStartDateChange}
                    ref={startDateInputRef}
                    className="w-full pl-10 pr-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-white"
                    style={{
                      color: "#495057",
                      backgroundColor: "#FFFFFF",
                      fontSize: "13px",
                      height: "100%",
                      appearance: "none",
                      WebkitAppearance: "none",
                      MozAppearance: "textfield",
                      backgroundImage: "none",
                      cursor: "pointer",
                    }}
                  />
                </div>
              </div>

              {/* End Date */}
              <div>
                <label
                  htmlFor="end-date"
                  className="block text-sm font-medium mb-1 text-[#6F6D7C]"
                >
                  End date
                </label>
                <div
                  className="relative flex items-center h-[39px] rounded-lg border"
                  style={{ borderColor: "#E2E2E5" }}
                  onClick={handleClickEndDate}
                >
                  <div className="absolute left-3 pointer-events-none">
                    <Calendar size={18} className="text-[#8C8A96]" />
                  </div>
                  <input
                    id="end-date"
                    type="date"
                    value={endDate}
                    onChange={handleEndDateChange}
                    ref={endDateInputRef}
                    min={startDate || undefined}
                    className="w-full pl-10 pr-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-white"
                    style={{
                      color: "#495057",
                      backgroundColor: "#FFFFFF",
                      fontSize: "13px",
                      height: "100%",
                      appearance: "none",
                      WebkitAppearance: "none",
                      MozAppearance: "textfield",
                      backgroundImage: "none",
                      cursor: "pointer",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="px-4 py-3 flex justify-center bg-white"
          style={{ borderTop: "1px solid #E1E5E9" }}
        >
          <button
            type="submit"
            disabled={loading}
            onClick={() => {
              GotoGeneration();
            }}
            className="flex items-center justify-center w-[165px] h-[40px] border-[0.5px] px-4 py-3 gap-1 rounded-[8px] opacity-100 text-white text-sm font-medium transition-opacity"
            style={{ backgroundColor: "#F1935C" }}
          >
            {loading ? "Loading..." : "Start"}
          </button>
        </div>
        <ErrorPopup error={error} onClose={() => setError(null)} />
      </ModalMod>
    </>
  );
};

export default CreateNewSessionModal;
